import { Product } from 'src/pages/deprecated/payment/types';
import { ChallengeAddOnProduct } from '../types/challenge.types';
import { ChallengePlanId } from 'src/pages/types';
import { PLANS } from 'src/pages/constants';
import { isUserFromAustralia } from 'src/lib/utils';
import { PaymentMethod } from '@stripe/stripe-js';

export const isAfterpayPaymentMethod = (
  paymentMethod?: PaymentMethod
): boolean => {
  if (!paymentMethod) return false;
  return (
    paymentMethod.type === 'afterpay_clearpay' ||
    paymentMethod.type === 'after_pay'
  );
};

export const isCardPaymentMethod = (paymentMethod?: PaymentMethod): boolean => {
  if (!paymentMethod) return false;
  return (
    !isAfterpayPaymentMethod(paymentMethod) &&
    (paymentMethod.type === 'card' || !!paymentMethod.card)
  );
};

export const getAddOnChallengeProducts = (products: Product[]) => {
  const filteredProducts = products.filter(product => {
    return Object.values(product.prices || {}).some(price => {
      return price.active && price.metadata?.isAddOnChallengeProduct === 'true';
    });
  });

  const allowedProducts = isUserFromAustralia()
    ? [PLANS.CHALLENGE_ONLY, PLANS.CHALLENGE_WITH_SUPPLEMENTS]
    : [PLANS.CHALLENGE_ONLY];

  return filteredProducts.filter(product =>
    allowedProducts.includes((product?.name || '') as PLANS)
  );
};

// Get Personal Coach Addon (Challenge) Product Price
export const getPersonalCoachPrice = (products: Product[]) => {
  const coachProduct = products.find(
    p => p.metadata?.['personal_coach'] === 'true'
  );

  if (!coachProduct) return undefined;

  const activePrice = Object.values(coachProduct.prices || {}).find(
    price => price.active
  );

  if (!activePrice) return undefined;

  return {
    priceId: activePrice.id,
    unitAmount: Number(activePrice.unitAmount), // converts cents to dollars
  };
};

export const mapToChallengeAddOnProducts = (
  products: Product[]
): ChallengeAddOnProduct[] => {
  const challengeProducts = getAddOnChallengeProducts(products);

  // Config mapping product.name → ChallengePlanId + description
  const planConfig: Record<
    string,
    { id: ChallengePlanId; name?: string; description: string }
  > = {
    [PLANS.CHALLENGE_WITH_SUPPLEMENTS]: {
      id: ChallengePlanId.CHALLENGE_WITH_SUPPLEMENTS,
      description: 'Full access to the 8 Week Challenge + \nProtein Powder',
    },
    [PLANS.CHALLENGE_ONLY]: {
      id: ChallengePlanId.ONLY_CHALLENGE,
      description: 'Full access to the 8 Week Challenge',
    },
    [PLANS.PERSONAL_COACH]: {
      id: ChallengePlanId.PERSON_COACH,
      name: 'Personal Nutrition Coach',
      description:
        'Take your transformation to the next level with 1:1 nutrition coaching',
    },
  };

  return challengeProducts
    .map(product => {
      const config = planConfig[product.name];
      if (!config) return null;

      const priceInfo = getChallengeProductPriceInfo(product);
      if (!priceInfo) return null;

      return {
        challengeId: config.id,
        product,
        name: config?.name ?? product.name,
        priceId: priceInfo.priceId,
        amount: priceInfo.price,
        description: config.description,
      };
    })
    .filter((p): p is ChallengeAddOnProduct => p !== null);
};

const getChallengeProductPriceInfo = (
  product: Product
): { priceId: string; price: number } | undefined => {
  const prices = product?.prices ?? {};

  const activePrice = Object.values(prices).find(price => price?.active);
  const priceAmount = Number(activePrice?.unitAmount);

  if (!activePrice?.id || isNaN(Number(activePrice?.unitAmount))) {
    return undefined;
  }

  return {
    priceId: activePrice.id,
    price: priceAmount,
  };
};

// ==============================
// Nutrition 1:1 Coaching Addon
// ==============================

// Get 1:1 Nutrition Coach Addon Product
export const getNutritionCoachProduct = (products: Product[]) => {
  return products.find(product => product.name === PLANS.NUTRITION_COACHING);
};

// Get 1:1 Nutrition Coach Addon Product Price
export const getNutritionCoachProductPrice = (products: Product[]) => {
  const nutritionCoachProduct = getNutritionCoachProduct(products);
  if (!nutritionCoachProduct) return undefined;

  return getChallengeProductPriceInfo(nutritionCoachProduct);
};
